/**
 * Email Template editor — full-page replacement for the cramped modal
 * that used to handle this surface. Two routes, one component:
 *
 *   /super-admin/email-templates/new        → create
 *   /super-admin/email-templates/:id/edit   → edit
 *
 * Layout: a two-column grid where the form sits on the left and the
 * live HTML preview sits sticky on the right. The Variables picker is
 * a proper section instead of a squeezed dropdown, and the HTML body
 * editor gets a tall textarea (no more 13-row constraint that the
 * modal forced).
 *
 * State + mutation logic ported wholesale from the previous
 * `TemplateDialog` so the API surface and validation rules are
 * identical — only the chrome changed.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Mail,
  X,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import {
  emailEventsResponse,
  emailTemplatesResponse,
} from '../../lib/types'
import { Button } from '../../components/ui/Button'
import { TextField } from '../../components/ui/Field'
import { formatNumber } from '../../lib/format'

/** Template keys are lowercase snake_case. Mirrors the rule the
 *  backend enforces in `modules.routes.js` so the inline validation
 *  surfaces the problem before submit. */
const KEY_RE = /^[a-z0-9_]+$/

/** Default empty template the create flow seeds. Spelled out as a
 *  constant so the reset-on-load behaviour for /new stays predictable. */
const EMPTY_FORM = {
  name: '',
  templateKey: '',
  subject: '',
  bodyHtml: '',
  variables: [],
  isActive: true,
}

/** Shared input chrome — duplicated here rather than re-imported so
 *  this page can render independently of the modal-era styles. */
const inputBase =
  'w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] ' +
  'bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] outline-none ' +
  'transition-colors focus:border-[color:var(--color-accent)]'

/** Wraps the body HTML in a minimal document with brand-tone styles
 *  so the preview iframe renders something readable even when the
 *  admin pastes a bare snippet. Identical to the helper from the
 *  former modal. */
/** Substitute ONLY the auto-injected platform branding so the preview
 *  shows the real header (logo + brand name) that subscribers will
 *  receive. Per-event tokens like `{{name}}`, `{{admin_name}}` are
 *  LEFT as visible placeholders on purpose — the editor's whole job
 *  is to let an admin see and edit those tokens. Showing them as
 *  fake-real names ("Hi Alex Morgan,") made admins worry that real
 *  emails would carry the sample data; this keeps the preview
 *  unambiguous. The Send Test dialog still seeds realistic sample
 *  values because those values actually leave the server. */
/** Cap for the HTML body. Mirrors `emailTemplateCreateDto.bodyHtml` on the
 *  API — the two must move together, since the server is what actually
 *  blocks the save. See that DTO for why 100k specifically. */
const BODY_MAX = 100_000

function applyPreviewSubstitutions(bodyHtml, branding) {
  const name = branding?.name || 'TaskMagix'
  const logoUrl = branding?.logoUrl || ''
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="" height="40" style="display:inline-block;height:40px;width:auto;border:0;" />`
    : ''
  return String(bodyHtml ?? '')
    .replaceAll('{{platform_logo_html}}', logoHtml)
    .replaceAll('{{platform_logo_url}}', logoUrl)
    .replaceAll('{{platform_name}}', name)
    .replaceAll('{{platform_support_email}}', branding?.supportEmail || '')
}

function buildPreviewDoc(bodyHtml, branding) {
  const safe = applyPreviewSubstitutions(bodyHtml, branding)
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
    body { font-family: 'Inter', system-ui, sans-serif; color: #1a2841; margin: 0; padding: 24px; background: #f0f8fc; }
    a { color: #006CA5; }
    img { max-width: 100%; height: auto; }
  </style></head><body>${safe}</body></html>`
}

export function EmailTemplateEditorPage() {
  const navigate = useNavigate()
  const params = useParams()
  const qc = useQueryClient()
  const isEdit = Boolean(params.id)
  const bodyRef = useRef(null)

  // ── Load existing template when editing ────────────────────────────────
  // Reuse the same list query the Templates tab fetches — saves a
  // round-trip and keeps the row source-of-truth in one place. When
  // the cache is cold (admin deep-linked to /:id/edit) we still
  // populate the form from the API response once it arrives.
  const listQuery = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const res = await api.get('/super-admin/email-templates')
      return emailTemplatesResponse.parse(res.data)
    },
    enabled: isEdit,
  })
  const target = useMemo(() => {
    if (!isEdit) return null
    return listQuery.data?.data?.find((t) => t.id === params.id) ?? null
  }, [isEdit, listQuery.data, params.id])

  // ── Event catalogue — drives the variable picker ───────────────────────
  const eventsQuery = useQuery({
    queryKey: ['email-events'],
    queryFn: async () => {
      const res = await api.get('/super-admin/email-events')
      return emailEventsResponse.parse(res.data)
    },
  })

  // ── Form state ─────────────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_FORM)
  const [varDraft, setVarDraft] = useState('')
  const [errors, setErrors] = useState({})
  const [showPreview, setShowPreview] = useState(true)
  const [varPickerOpen, setVarPickerOpen] = useState(false)
  // Natural size of the rendered email, measured when the preview iframe
  // loads. The iframe is sized to the CONTENT and the wrapper div owns
  // scrolling — an iframe's own scrollbars are dead under
  // `pointer-events-none` (QA: "horizontal scroll bar not working"),
  // whereas the wrapper's scrollbars live in the parent document and
  // stay fully interactive. Same measuring trick as SentEmailsView's
  // preview dialog.
  const [frameSize, setFrameSize] = useState({ width: null, height: 560 })
  // Tracks the id we last hydrated for so an admin navigating between
  // /1/edit and /2/edit gets the right row's data without manual
  // reset logic. Resets the form when the id changes too.
  const hydratedFor = useRef(null)

  useEffect(() => {
    if (!isEdit) {
      // Fresh create — wipe the form whenever the route transitions
      // into /new (e.g. from /:id/edit).
      if (hydratedFor.current !== '__new__') {
        setForm(EMPTY_FORM)
        setErrors({})
        hydratedFor.current = '__new__'
      }
      return
    }
    if (target && hydratedFor.current !== target.id) {
      setForm({
        name: target.name ?? '',
        templateKey: target.templateKey ?? '',
        subject: target.subject ?? '',
        bodyHtml: target.bodyHtml ?? '',
        variables: target.variables ?? [],
        isActive: target.isActive ?? true,
      })
      setErrors({})
      hydratedFor.current = target.id
    }
  }, [isEdit, target])

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) {
      const { [key]: _omit, ...rest } = errors
      void _omit
      setErrors(rest)
    }
  }

  function addVariable() {
    const name = varDraft.trim().replace(/[^a-zA-Z0-9_]/g, '')
    if (!name) return
    if (!form.variables.includes(name)) {
      setField('variables', [...form.variables, name])
    }
    setVarDraft('')
  }

  function removeVariable(name) {
    setField(
      'variables',
      form.variables.filter((v) => v !== name),
    )
  }

  function toggleVariable(name) {
    setField(
      'variables',
      form.variables.includes(name)
        ? form.variables.filter((v) => v !== name)
        : [...form.variables, name],
    )
  }

  /** Insert `{{name}}` at the textarea's current cursor position.
   *  Falls back to appending when the ref isn't mounted yet. */
  function insertVariable(name) {
    const token = `{{${name}}}`
    const ta = bodyRef.current
    if (!ta) {
      setField('bodyHtml', form.bodyHtml + token)
      return
    }
    const start = ta.selectionStart ?? form.bodyHtml.length
    const end = ta.selectionEnd ?? start
    setField(
      'bodyHtml',
      form.bodyHtml.slice(0, start) + token + form.bodyHtml.slice(end),
    )
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + token.length
      ta.setSelectionRange(pos, pos)
    })
  }

  function validate() {
    const next = {}
    if (!form.name.trim()) next.name = 'Template name is required.'
    if (!form.templateKey.trim()) {
      next.templateKey = 'Template key is required.'
    } else if (!KEY_RE.test(form.templateKey.trim())) {
      next.templateKey =
        'Use lowercase letters, numbers and underscores only.'
    }
    if (!form.subject.trim()) next.subject = 'Subject is required.'
    if (!form.bodyHtml.trim()) next.bodyHtml = 'Email body is required.'
    else if (form.bodyHtml.length > BODY_MAX) {
      next.bodyHtml = `Email body must be at most ${formatNumber(BODY_MAX)} characters.`
    }
    return next
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim(),
        templateKey: form.templateKey.trim(),
        subject: form.subject.trim(),
        bodyHtml: form.bodyHtml,
        variables: form.variables,
        isActive: form.isActive,
      }
      if (isEdit) {
        const res = await api.patch(
          `/super-admin/email-templates/${params.id}`,
          body,
        )
        return res.data
      }
      const res = await api.post('/super-admin/email-templates', body)
      return res.data
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Template updated successfully.' : 'Template created successfully.')
      void qc.invalidateQueries({ queryKey: ['email-templates'] })
      void qc.invalidateQueries({ queryKey: ['email-assignments'] })
      navigate('/super-admin/email-templates')
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) setErrors(fields)
      else toast.error(message)
    },
  })

  // Fetch the platform settings so the preview can substitute the
  // auto-injected branding variables ({{platform_name}},
  // {{platform_logo_html}} etc.) instead of showing them as literal
  // tokens. Same endpoint the Platform Settings page uses, so the
  // React Query cache is shared and we don't pay an extra request
  // when the admin is bouncing between the two screens.
  const settingsQuery = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const res = await api.get('/super-admin/settings')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })
  const branding = useMemo(() => {
    const rows = settingsQuery.data?.data ?? settingsQuery.data ?? []
    const arr = Array.isArray(rows) ? rows : []
    const byKey = new Map(arr.map((r) => [r._id ?? r.id ?? r.key, r.value]))
    return {
      name: byKey.get('platform.name') || 'TaskMagix',
      logoUrl: byKey.get('platform.brand_logo_url') || '',
      supportEmail: byKey.get('platform.support_email') || '',
    }
  }, [settingsQuery.data])

  const previewDoc = useMemo(
    () => buildPreviewDoc(form.bodyHtml, branding),
    [form.bodyHtml, branding],
  )

  // ── Build the deduped variable picker rows once per data load ──────────
  // Walk the event catalogue, record each variable once, and keep the
  // events that provide it as "Used by" badges. Sorted by event-count
  // descending so shared variables ({{name}}, {{email}}) surface at the
  // top of the picker.
  //
  // CRITICAL: this hook MUST sit above the conditional early returns
  // below (loading / not-found branches). Originally it lived after
  // those returns, which violates the React rules-of-hooks invariant
  // that the same hooks run in the same order on every render — when
  // the page transitioned from "loading" → "loaded" the hook count
  // changed and React threw "Rendered more hooks than during the
  // previous render," crashing the editor.
  const variableRows = useMemo(() => {
    const map = new Map()
    for (const g of eventsQuery.data?.groups ?? []) {
      for (const ev of g.events) {
        for (const v of ev.variables) {
          const list = map.get(v) ?? []
          list.push(ev.label)
          map.set(v, list)
        }
      }
    }
    return Array.from(map.entries())
      .map(([name, events]) => ({ name, events }))
      .sort((a, b) => {
        if (b.events.length !== a.events.length) {
          return b.events.length - a.events.length
        }
        return a.name.localeCompare(b.name)
      })
  }, [eventsQuery.data])

  // ── Loading state (deep-link to /:id/edit, cache cold) ─────────────────
  if (isEdit && listQuery.isLoading) {
    return (
      <div className="p-8 text-sm text-[color:var(--color-ink-muted)]">
        Loading template…
      </div>
    )
  }
  if (isEdit && !listQuery.isLoading && !target) {
    return (
      <div className="p-8">
        <Breadcrumb
          current="Template not found"
          homePath="/super-admin/email-templates"
          homeLabel="Email Templates"
        />
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] mt-4 mb-2">
          Template Not Found
        </h1>
        <p className="text-sm text-[color:var(--color-ink-muted)] mb-6">
          The template you tried to edit doesn&apos;t exist or has been deleted.
        </p>
        <Button onClick={() => navigate('/super-admin/email-templates')}>
          <ArrowLeft size={14} />
          Back to Templates
        </Button>
      </div>
    )
  }

  // `variableRows` is now computed BEFORE the early returns above
  // (see the useMemo near the top of this function). The duplicate
  // declaration that previously lived here was removed to fix the
  // hook-order crash.

  return (
    <div>
      <Breadcrumb
        current={isEdit ? 'Edit Template' : 'New Template'}
        homePath="/super-admin/email-templates"
        homeLabel="Email Templates"
      />

      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Mail size={22} className="text-[color:var(--color-accent-strong)]" />
            {isEdit ? 'Edit Email Template' : 'New Email Template'}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Write the email. Link it on the{' '}
            <button
              type="button"
              onClick={() =>
                navigate('/super-admin/email-templates?tab=assignments')
              }
              className="text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
            >
              Assignments
            </button>{' '}
            tab.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="secondary"
            onClick={() => navigate('/super-admin/email-templates')}
            disabled={saveMutation.isPending}
          >
            <ArrowLeft size={14} />
            Cancel
          </Button>
          <Button
            form="email-template-form"
            type="submit"
            loading={saveMutation.isPending}
          >
            {isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
        </div>
      </div>

      <form
        id="email-template-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (saveMutation.isPending) return
          const next = validate()
          setErrors(next)
          if (Object.keys(next).length === 0) saveMutation.mutate()
        }}
        noValidate
        // Two-column outer grid: Form (left, ~7 cols) + Preview (right,
        // ~5 cols, sticky). Collapses to a single column on narrow
        // viewports so the preview drops below the form. Standard
        // ratio used by every other create/edit page on the platform.
        className="grid grid-cols-1 lg:grid-cols-12 gap-6"
      >
        {/* ── Left column: form fields ──────────────────────────────── */}
        <div className="lg:col-span-7 space-y-6">
          {/* Basics card */}
          <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-4">
              Basics
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="Template Name"
                required
                placeholder="Enter a Template Name"
                maxLength={120}
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                error={errors.name}
              />
              <TextField
                label="Template Key"
                required
                placeholder="Enter the Template Key"
                maxLength={80}
                value={form.templateKey}
                onChange={(e) =>
                  setField('templateKey', e.target.value.toLowerCase())
                }
                error={errors.templateKey}
                disabled={isEdit}
                helperText={
                  isEdit
                    ? 'The key is locked after creation.'
                    : 'A unique identifier for this template.'
                }
              />
            </div>
            <div className="mt-4">
              <TextField
                label="Subject"
                required
                placeholder="Enter a Subject"
                maxLength={300}
                value={form.subject}
                onChange={(e) => setField('subject', e.target.value)}
                error={errors.subject}
              />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm">
                Active — available for events to use
              </span>
            </label>
          </section>

          {/* Variables card */}
          <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-1">
              Dynamic Variables
            </h2>
            <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
              Pick the variables your event provides from the catalogue
              below, or type a custom one. Click a saved chip to insert it
              into the email body at the cursor position.
            </p>

            {/* Picker dropdown — flat deduped list with "Used by" badges */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setVarPickerOpen((o) => !o)}
                className="w-full flex items-center justify-between h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm cursor-pointer hover:border-[color:var(--color-ink-muted)]"
              >
                <span className="text-[color:var(--color-ink-muted)]">
                  {form.variables.length > 0
                    ? `${form.variables.length} variable${form.variables.length === 1 ? '' : 's'} selected`
                    : 'Choose variables from your events…'}
                </span>
                <ChevronDown
                  size={16}
                  className={
                    'shrink-0 transition-transform ' +
                    (varPickerOpen ? 'rotate-180' : '')
                  }
                />
              </button>
              {varPickerOpen ? (
                <div className="mt-1 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] max-h-96 overflow-y-auto p-2.5">
                  {eventsQuery.isLoading ? (
                    <p className="text-xs text-[color:var(--color-ink-muted)]">
                      Loading…
                    </p>
                  ) : variableRows.length === 0 ? (
                    <p className="text-xs text-[color:var(--color-ink-muted)]">
                      No variables defined yet.
                    </p>
                  ) : (
                    // Grid layout — 3 columns on lg+, 2 on md, 1 on
                    // mobile. Now that each row is just a checkbox +
                    // variable name (the per-event chips were dropped),
                    // a long flat vertical list wastes a lot of
                    // whitespace. The grid keeps the picker
                    // scannable when a tenant has 30+ variables
                    // without forcing the user to scroll.
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                      {variableRows.map(({ name }) => {
                        const checked = form.variables.includes(name)
                        return (
                          <li key={name}>
                            <button
                              type="button"
                              onClick={() => toggleVariable(name)}
                              className={
                                'w-full flex items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors cursor-pointer ' +
                                (checked
                                  ? 'bg-[color:var(--color-accent-bg)] hover:bg-[color:var(--color-accent-bg)]'
                                  : 'hover:bg-[color:var(--color-card-hover)]')
                              }
                            >
                              <span
                                className={
                                  'inline-flex items-center justify-center h-4 w-4 rounded border shrink-0 ' +
                                  (checked
                                    ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white'
                                    : 'border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)]')
                                }
                                aria-hidden="true"
                              >
                                {checked ? <Check size={11} /> : null}
                              </span>
                              <span
                                className={
                                  'text-xs font-medium tabular-nums shrink-0 ' +
                                  (checked
                                    ? 'text-[color:var(--color-accent-strong)]'
                                    : 'text-[color:var(--color-ink-strong)]')
                                }
                              >
                                {`{{${name}}}`}
                              </span>
                              {/* Per-event "used by" chips removed by
                               *  request — the row now shows only the
                               *  checkbox + variable name so the
                               *  picker stays scannable when a single
                               *  variable is reused across 10+ events.
                               *  The mapping is still useful elsewhere
                               *  (the Email Events catalogue page
                               *  surfaces it the other way: per event
                               *  → list of variables) so we're not
                               *  losing the relationship — just
                               *  hiding the noise here.
                               *
                               *  Keep the local `events` array in the
                               *  loop in case you want to re-surface
                               *  it via a hover tooltip later. */}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>

            {/* Custom variable input */}
            <div className="flex gap-2">
              <input
                id="tpl-var"
                value={varDraft}
                onChange={(e) => setVarDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    addVariable()
                  }
                }}
                placeholder="Or type a custom variable…"
                className={inputBase}
              />
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={addVariable}
              >
                Add
              </Button>
            </div>

            {/* Selected chips — click name to insert into body, X to remove */}
            {form.variables.length > 0 ? (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                  In this template
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {form.variables.map((v) => (
                    <span
                      key={v}
                      className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] pl-2 pr-1 py-0.5 text-xs"
                    >
                      <button
                        type="button"
                        onClick={() => insertVariable(v)}
                        title={`Insert {{${v}}} into the body at cursor`}
                        className="font-medium text-[color:var(--color-accent-strong)] cursor-pointer hover:underline"
                      >
                        {`{{${v}}}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeVariable(v)}
                        aria-label={`Remove ${v}`}
                        title="Remove"
                        className="rounded-full p-0.5 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card)] cursor-pointer"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          {/* Body HTML card */}
          <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
                  Email Body (HTML)
                </h2>
                <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                  Paste raw HTML. Use inline styles for best email-client
                  compatibility.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview((p) => !p)}
                className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
              >
                {showPreview ? (
                  <>
                    <EyeOff size={12} />
                    Hide preview
                  </>
                ) : (
                  <>
                    <Eye size={12} />
                    Show preview
                  </>
                )}
              </button>
            </div>
            <textarea
              id="tpl-body"
              ref={bodyRef}
              value={form.bodyHtml}
              onChange={(e) => setField('bodyHtml', e.target.value)}
              rows={24}
              placeholder={
                '<h1>Hello {{name}}</h1>\n<p>Your code is {{otp}}.</p>'
              }
              className={
                'w-full px-3 py-2 rounded-lg border bg-[color:var(--color-card)] text-[color:var(--color-ink)] font-mono text-xs leading-relaxed outline-none transition-colors focus:border-[color:var(--color-accent)] resize-y ' +
                (errors.bodyHtml
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
            />
            {/* Message on the left, live count on the right — the same
              *  footer row the Announcements composer uses. The field
              *  SOFT-limits (no DOM `maxLength`), matching every other
              *  field in the app: it keeps accepting input and tells you
              *  you're over rather than silently swallowing keystrokes. */}
            <div className="mt-2 flex justify-between gap-4 text-xs">
              <span
                role={errors.bodyHtml ? 'alert' : undefined}
                className={
                  errors.bodyHtml
                    ? 'text-[color:var(--color-priority-urgent)]'
                    : 'text-[color:var(--color-ink-muted)]'
                }
              >
                {errors.bodyHtml ?? ' '}
              </span>
              <span
                className={
                  'shrink-0 tabular-nums ' +
                  (form.bodyHtml.length > BODY_MAX
                    ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                    : 'text-[color:var(--color-ink-muted)]')
                }
              >
                {formatNumber(form.bodyHtml.length)}/
                {formatNumber(BODY_MAX)}
              </span>
            </div>
          </section>
        </div>

        {/* ── Right column: sticky preview ────────────────────────────── */}
        {showPreview ? (
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-6">
              <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
                <div className="px-5 py-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex items-center gap-2">
                  <Eye
                    size={14}
                    className="text-[color:var(--color-ink-muted)]"
                  />
                  <h3 className="font-display text-sm text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
                    Preview
                  </h3>
                  <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                    {form.subject || 'No subject'}
                  </span>
                </div>
                {/* pointer-events-none — previews are non-interactive;
                 *  the template CTAs would otherwise be clickable (same
                 *  QA ruling as the Sent-email preview). That also makes
                 *  the iframe's OWN scrollbars inert, so this wrapper div
                 *  is the scroller instead: the iframe is sized to the
                 *  email's natural width/height (measured on load — hence
                 *  `allow-same-origin`, which lets the parent read the
                 *  snapshot without enabling scripts, exactly like the
                 *  Sent-email preview) and wheel events fall through the
                 *  inert iframe to this div. */}
                <div className="max-h-[560px] overflow-auto bg-white">
                  <iframe
                    title="Email preview"
                    sandbox="allow-same-origin"
                    srcDoc={previewDoc}
                    onLoad={(e) => {
                      const doc = e.target.contentDocument?.documentElement
                      if (!doc) return
                      const width = doc.scrollWidth
                      const height = doc.scrollHeight
                      setFrameSize((prev) =>
                        prev.width === width && prev.height === height
                          ? prev
                          : { width, height },
                      )
                    }}
                    style={{
                      width: frameSize.width ?? '100%',
                      minWidth: '100%',
                      height: frameSize.height,
                    }}
                    className="block bg-white pointer-events-none"
                  />
                </div>
              </div>
              <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
                Placeholders like <code>{'{{name}}'}</code> stay literal in
                this preview. At send time the event passes the real value.
              </p>
            </div>
          </aside>
        ) : null}
      </form>
    </div>
  )
}
